import { Attachment, Button } from '@billy/design-system'
import { useCallback } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { ChooseFileDropdown } from '@features/attachments/elements/ChooseFileDropdown'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../../../hooks'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { DrawerSection } from '../DrawerSection'
import { AddAttachmentSection } from './AddAttachmentSection'
import { AttachmentCard } from './AttachmentCard'
import * as Styled from './styles'

interface AttachmentsSectionProps {
  attachments?: Attachment[]
  isLoading?: boolean
  isVoided?: boolean
  onAttachmentSelect: (attachment: Attachment) => void
  onFilesDropped: (filesAccepted: File[], filesRejected?: FileRejection[]) => void
  onRemove: (attachmentId: string) => void
  scope: Scope
  trackingContext: TrackingContext
}

export const AttachmentsSection = ({
  attachments,
  isLoading,
  isVoided,
  onAttachmentSelect,
  onFilesDropped,
  onRemove,
  scope,
  trackingContext,
}: AttachmentsSectionProps) => {
  const { track } = useSegment()
  const { t } = useTranslation()

  const { originatorType, transactionId } = useTransactionDrawerContext()

  const trackAddAttachment = useCallback(() => {
    track('xxx - Bookkeeping - Ledger - Drawer', {
      action: 'Add attachment',
      transactionId,
      transactionType: originatorType,
    })
  }, [originatorType, track, transactionId])

  const handleFilesDropped = useCallback(
    (filesAccepted: File[], filesRejected?: FileRejection[]) => {
      onFilesDropped(filesAccepted, filesRejected)
      trackAddAttachment()
    },
    [onFilesDropped, trackAddAttachment],
  )

  const handleAttachmentSelect = useCallback(
    (attachment: Attachment) => {
      onAttachmentSelect(attachment)
      trackAddAttachment()
    },
    [onAttachmentSelect, trackAddAttachment],
  )

  if (!attachments || attachments.length === 0) {
    if (isVoided) {
      return null
    }

    return (
      <AddAttachmentSection
        isLoading={isLoading}
        onAttachmentSelect={handleAttachmentSelect}
        onFilesDropped={handleFilesDropped}
        scope={scope}
        trackingContext={trackingContext}
      />
    )
  }

  return (
    <DrawerSection title={t('ledger.attachments')}>
      {attachments.map((attachment) => (
        <AttachmentCard key={attachment.id} attachment={attachment} onRemove={onRemove} />
      ))}
      {!isVoided && (
        <Styled.DropdownButtonWrapper>
          <ChooseFileDropdown
            onFileFromDeviceSelect={handleFilesDropped}
            onFileFromUploadsSelect={handleAttachmentSelect}
            attachmentsIdsChosen={attachments.map((attachment) => attachment.id)}
            trigger={
              <Button icon="plus" variant="text" loading={isLoading} iconRight="chevronDown">
                {t('ledger.attachments.drawer')}
              </Button>
            }
          />
        </Styled.DropdownButtonWrapper>
      )}
    </DrawerSection>
  )
}
